import type {
  SarAddress,
  SarContactInfo,
  SarDigerKisi,
  SarDigerKisiAddress,
  SarFieldInfoAdditionalPayload,
  SarFieldInfoAdditionalPayloadSection,
  SarGercekKisi,
  SarHesap,
  SarHesapSahibi,
  SarIdentityDocument,
  SarSupheliIslem,
  SarSupheliIslemAddress,
} from './SarInterfaces';
import type { SarFieldInfoDefinition, SarFieldInfoSection } from './config/SarFieldInfoConfiguration';
import moment from 'moment';

class SarParser {
  private static getElementText(parent: Element, tagName: string): string {
    const element = parent.getElementsByTagName(tagName).item(0);
    return element?.textContent?.trim() ?? '';
  }

  public static parseGercekKisiler(xmlContent?: string): SarGercekKisi[] {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return [];
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const personNodes = Array.from(xmlDoc.getElementsByTagName('gercekkisi'));
    return personNodes.map((personNode, index) => ({
      id: `gercekkisi-${index}`,
      connectionId: this.getElementText(personNode, 'islemlebaglantisi'),
      connectionDescription: this.getElementText(personNode, 'islemlebaglantisiaciklama'),
      firstName: this.getElementText(personNode, 'adi'),
      lastName: this.getElementText(personNode, 'soyadi'),
      nationalId: this.getElementText(personNode, 'tckn'),
      taxId: this.getElementText(personNode, 'vkn'),
      foreignId: this.getElementText(personNode, 'ykn'),
      birthDate: this.getElementText(personNode, 'dogumtarihi'),
      birthPlace: this.getElementText(personNode, 'dogumyeri'),
      motherName: this.getElementText(personNode, 'anaadi'),
      fatherName: this.getElementText(personNode, 'babaadi'),
      nationality: this.getElementText(personNode, 'uyrugu'),
      gender: this.getElementText(personNode, 'cinsiyeti'),
      identityDocuments: this.parseIdentityDocuments(personNode),
      profession: this.getElementText(personNode, 'meslek'),
      additionalInfo: this.getElementText(personNode, 'ekbilgi'),
      addresses: this.parseAddresses(personNode),
      contactInfos: this.parseContactInfos(personNode),
      fakeIdentityFlag: this.getElementText(personNode, 'kimlikbilgilerisahte'),
      victimFlag: this.getElementText(personNode, 'magdur'),
      identificationType: this.getElementText(personNode, 'kimliktespitturu'),
    }));
  }

  private static parseIdentityDocuments(personNode: Element): SarIdentityDocument[] {
    const identityDocumentsParent = personNode.getElementsByTagName('kimlikbelgeleri').item(0);
    if (!identityDocumentsParent) {
      return [];
    }
    const documentNodes = Array.from(identityDocumentsParent.getElementsByTagName('kimlikbelgesi'));
    return documentNodes.map((documentNode, index) => ({
      id: `kimlikbelgesi-${index}`,
      documentType: this.getElementText(documentNode, 'belgeturu'),
      documentNumber: this.getElementText(documentNode, 'belgenumarasi'),
      expiryDate: this.getElementText(documentNode, 'gecerliliktarihi'),
    }));
  }

  private static parseAddresses(personNode: Element): SarAddress[] {
    const addressesParent = personNode.getElementsByTagName('adresler').item(0);
    if (!addressesParent) {
      return [];
    }
    const addressNodes = Array.from(addressesParent.getElementsByTagName('adres'));
    return addressNodes.map((addressNode, index) => ({
      id: `adres-${index}`,
      type: addressNode.getAttribute('tip')?.trim() ?? '',
      line: this.getElementText(addressNode, 'adressatir'),
      postalCode: this.getElementText(addressNode, 'postakodu'),
      cityCode: this.getElementText(addressNode, 'ilkodu'),
      districtCode: this.getElementText(addressNode, 'ilcekodu'),
      countryCode: this.getElementText(addressNode, 'ulkekodu'),
    }));
  }

  private static parseContactInfos(personNode: Element): SarContactInfo[] {
    const contactInfosParent = personNode.getElementsByTagName('iletisimbilgileri').item(0);
    if (!contactInfosParent) {
      return [];
    }
    const contactNodes = Array.from(contactInfosParent.getElementsByTagName('iletisimbilgisi'));
    return contactNodes.map((contactNode, index) => ({
      id: `iletisimbilgisi-${index}`,
      tip: contactNode.getAttribute('tip')?.trim() ?? '',
      value: contactNode.textContent?.trim() ?? '',
    }));
  }

  public static parseDigerKisiler(xmlContent?: string): SarDigerKisi[] {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return [];
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const personNodes = Array.from(xmlDoc.getElementsByTagName('digerkisi'));
    return personNodes.map((personNode, index) => ({
      id: `digerkisi-${index}`,
      connectionId: this.getElementText(personNode, 'islemlebaglantisi'),
      connectionDescription: this.getElementText(personNode, 'islemlebaglantisiaciklama'),
      establishmentType: this.getElementText(personNode, 'kurulusnevi'),
      title: this.getElementText(personNode, 'unvan'),
      businessName: this.getElementText(personNode, 'isletmeadi'),
      taxId: this.getElementText(personNode, 'vkn'),
      registryNo: this.getElementText(personNode, 'sicilno'),
      activitySubject: this.getElementText(personNode, 'faaliyetkonusu'),
      countryCode: this.getElementText(personNode, 'ulkekodu'),
      cityCode: this.getElementText(personNode, 'ilkodu'),
      districtCode: this.getElementText(personNode, 'ilcekodu'),
      establishmentDate: this.getElementText(personNode, 'kurulustarihi'),
      website: this.getElementText(personNode, 'websitesi'),
      additionalInfo: this.getElementText(personNode, 'ekbilgi'),
      addresses: this.parseDigerKisiAddresses(personNode),
      contactInfos: this.parseDigerKisiContactInfos(personNode),
      fakeIdentityFlag: this.getElementText(personNode, 'kimlikbilgilerisahte'),
      victimFlag: this.getElementText(personNode, 'magdur'),
    }));
  }

  private static parseDigerKisiAddresses(personNode: Element): SarDigerKisiAddress[] {
    const addressesParent = personNode.getElementsByTagName('adresler').item(0);
    if (!addressesParent) {
      return [];
    }
    const addressNodes = Array.from(addressesParent.getElementsByTagName('adres'));
    return addressNodes.map((addressNode, index) => ({
      id: `adres-${index}`,
      type: addressNode.getAttribute('tip')?.trim() ?? '',
      line: this.getElementText(addressNode, 'adressatir'),
      postalCode: this.getElementText(addressNode, 'postakodu'),
      cityCode: this.getElementText(addressNode, 'ilkodu'),
      districtCode: this.getElementText(addressNode, 'ilcekodu'),
      countryCode: this.getElementText(addressNode, 'ulkekodu'),
    }));
  }

  private static parseDigerKisiContactInfos(personNode: Element): SarContactInfo[] {
    const contactInfosParent = personNode.getElementsByTagName('iletisimbilgileri').item(0);
    if (!contactInfosParent) {
      return [];
    }
    const contactNodes = Array.from(contactInfosParent.getElementsByTagName('iletisimbilgisi'));
    return contactNodes.map((contactNode, index) => ({
      id: `iletisimbilgisi-${index}`,
      tip: contactNode.getAttribute('tip')?.trim() ?? '',
      value: contactNode.textContent?.trim() ?? '',
    }));
  }

  public static parseSupheliIslemler(xmlContent?: string): SarSupheliIslem[] {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return [];
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const islemNodes = Array.from(xmlDoc.getElementsByTagName('supheliislem'));
    return islemNodes.map((islemNode, index) => ({
      id: `supheliislem-${index}`,
      transactionType: this.getElementText(islemNode, 'islemcesidi'),
      transactionDate1: this.getElementText(islemNode, 'islemtarihi1'),
      transactionDate2: this.getElementText(islemNode, 'islemtarihi2'),
      transactionStatus: this.getElementText(islemNode, 'islemdurumu'),
      relatedHesap: this.getElementText(islemNode, 'hesababagli'),
      amountType: this.getElementText(islemNode, 'tutartp'),
      amount: this.getElementText(islemNode, 'tutar'),
      currency: this.getElementText(islemNode, 'parabirimi'),
      transactionCategory: this.getElementText(islemNode, 'islemturu'),
      channel: this.getElementText(islemNode, 'islemkanali'),
      ipv4: this.getElementText(islemNode, 'ipv4'),
      port: this.getElementText(islemNode, 'port'),
      description: this.getElementText(islemNode, 'islemaciklamasi'),
      countryCode: this.getElementText(islemNode, 'ulkekodu'),
      cityCode: this.getElementText(islemNode, 'ilkodu'),
      districtCode: this.getElementText(islemNode, 'ilcekodu'),
      executionLocation: this.getElementText(islemNode, 'islemingerceklestirildigiyer'),
      triggerLocation: this.getElementText(islemNode, 'islemintetiklendigiyer'),
      mediationStatus: this.getElementText(islemNode, 'islemearaciliketmedurumu'),
      mediationInstitution: this.getElementText(islemNode, 'islemearacilikedenkurum'),
      addresses: this.parseSupheliIslemAddresses(islemNode),
      contactInfos: this.parseSupheliIslemContactInfos(islemNode),
    }));
  }

  private static parseSupheliIslemAddresses(islemNode: Element): SarSupheliIslemAddress[] {
    const addressesParent = islemNode.getElementsByTagName('adresler').item(0);
    if (!addressesParent) {
      return [];
    }
    const addressNodes = Array.from(addressesParent.getElementsByTagName('adres'));
    return addressNodes.map((addressNode, index) => ({
      id: `adres-${index}`,
      type: addressNode.getAttribute('tip')?.trim() ?? '',
      line: this.getElementText(addressNode, 'adressatir'),
      postalCode: this.getElementText(addressNode, 'postakodu'),
      cityCode: this.getElementText(addressNode, 'ilkodu'),
      districtCode: this.getElementText(addressNode, 'ilcekodu'),
      countryCode: this.getElementText(addressNode, 'ulkekodu'),
    }));
  }

  private static parseSupheliIslemContactInfos(islemNode: Element): SarContactInfo[] {
    const contactInfosParent = islemNode.getElementsByTagName('iletisimbilgileri').item(0);
    if (!contactInfosParent) {
      return [];
    }
    const contactNodes = Array.from(contactInfosParent.getElementsByTagName('iletisimbilgisi'));
    return contactNodes.map((contactNode, index) => ({
      id: `iletisimbilgisi-${index}`,
      tip: contactNode.getAttribute('tip')?.trim() ?? '',
      value: contactNode.textContent?.trim() ?? '',
    }));
  }

  public static parseSupheKategorileri(xmlContent?: string): string[] {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return [];
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const parent = xmlDoc.getElementsByTagName('suphekategorileri').item(0);
    if (!parent) {
      return [];
    }
    const categoryNodes = Array.from(parent.getElementsByTagName('suphekategorisi'));
    return categoryNodes.map((categoryNode) => categoryNode.textContent?.trim() ?? '');
  }

  public static parseHesaplar(xmlContent?: string): SarHesap[] {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return [];
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const hesapNodes = Array.from(xmlDoc.getElementsByTagName('hesap'));
    return hesapNodes.map((hesapNode, index) => ({
      id: `hesap-${index}`,
      personOrCompanyHesap: this.getElementText(hesapNode, 'sahisfirmasihesabi'),
      hesapType: this.getElementText(hesapNode, 'hesapturu'),
      hesapNumber: this.getElementText(hesapNode, 'hesapno'),
      iban: this.getElementText(hesapNode, 'iban'),
      openingDate: this.getElementText(hesapNode, 'acilistarihi'),
      closingDate: this.getElementText(hesapNode, 'kapanistarihi'),
      lastTransactionDate: this.getElementText(hesapNode, 'sonislemtarihi'),
      balanceType: this.getElementText(hesapNode, 'bakiyetp'),
      balance: this.getElementText(hesapNode, 'bakiye'),
      currency: this.getElementText(hesapNode, 'parabirimi'),
      restrictionType: this.getElementText(hesapNode, 'kisitlamaturu'),
      restrictionDescription: this.getElementText(hesapNode, 'kisitlamadigeraciklama'),
      owners: this.parseHesapSahipleris(hesapNode),
    }));
  }

  private static parseHesapSahipleris(hesapNode: Element): SarHesapSahibi[] {
    const ownersParent = hesapNode.getElementsByTagName('hesapsahipleri').item(0);
    if (!ownersParent) {
      return [];
    }
    const ownerNodes = Array.from(ownersParent.getElementsByTagName('hesapsahibi'));
    return ownerNodes.map((ownerNode, index) => ({
      id: `hesapsahibi-${index}`,
      victimFlag: this.getElementText(ownerNode, 'magdur'),
      fullNameOrTitle: this.getElementText(ownerNode, 'adsoyadunvan'),
      nationalId: this.getElementText(ownerNode, 'tckn'),
      taxId: this.getElementText(ownerNode, 'vkn'),
      foreignId: this.getElementText(ownerNode, 'ykn'),
      passport: this.getElementText(ownerNode, 'pasaport'),
    }));
  }

  public static parseDateString(dateString: string): Date | null {
    if (!dateString || dateString.trim().length === 0) {
      return null;
    }
    const parsed = moment(dateString, 'DD/MM/YYYY', true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    return null;
  }

  private static mapSection(section: SarFieldInfoAdditionalPayloadSection, fallbackIndex: number): SarFieldInfoSection {
    const safeId = section.id ?? `section-${fallbackIndex}`;
    const safeIcon = section.icon ?? 'info';
    return {
      id: safeId,
      title: section.title,
      description: section.description,
      icon: safeIcon,
      badge: section.badge,
    };
  }

  public static parseAdditionalInfo(payload?: string): SarFieldInfoDefinition | undefined {
    if (!payload) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(payload) as SarFieldInfoAdditionalPayload;
      if (!parsed.sections || parsed.sections.length === 0) {
        return undefined;
      }
      const sections = parsed.sections.map((section: SarFieldInfoAdditionalPayloadSection, index: number) => this.mapSection(section, index));
      const title = parsed.title ?? sections[0].title;
      if (!title) {
        return undefined;
      }
      return {
        title,
        sections,
      };
    } catch (_error: unknown) {
      return undefined;
    }
  }
}

export default SarParser;
